import { useEffect, useRef, useState } from "react"
import { Link, router } from "@inertiajs/react"
import { Loader2, MapPin, Navigation, Search, Store, X } from "lucide-react"
import { ScreenHeader } from "@/components/ui/headers"
import { cn } from "@/lib/utils"
import type { DirectoryLocation } from "./types"
import { t } from "@/lib/i18n"

type DirectoryScreenProps = {
  query: string
  results: DirectoryLocation[]
  nearby: boolean
  /**
   * Where the debounced search refetches from. The directory is rendered by two
   * routes — `/search` and the no-context home at `/` — and each has to search
   * against itself or typing would navigate the diner off the screen.
   */
  searchPath?: string
}

type Coords = { lat: number; lng: number }

export function DirectoryScreen({
  query,
  results,
  nearby,
  searchPath = "/search",
}: DirectoryScreenProps) {
  const [value, setValue] = useState(query)
  const [coords, setCoords] = useState<Coords | null>(null)
  const [locating, setLocating] = useState(false)
  const [geoError, setGeoError] = useState(false)
  const firstRun = useRef(true)

  // Debounced server-side fetch whenever the query text or active coordinates change.
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false
      return
    }
    const handle = setTimeout(() => {
      router.get(
        searchPath,
        { q: value.trim() || undefined, lat: coords?.lat, lng: coords?.lng },
        { preserveState: true, preserveScroll: true, replace: true, only: ["results", "query", "nearby"] }
      )
    }, 300)
    return () => clearTimeout(handle)
  }, [value, coords, searchPath])

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      setGeoError(true)
      return
    }
    setLocating(true)
    setGeoError(false)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCoords({ lat: position.coords.latitude, lng: position.coords.longitude })
        setLocating(false)
      },
      () => {
        setGeoError(true)
        setLocating(false)
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    )
  }

  const clearProximity = () => {
    setGeoError(false)
    if (coords) {
      // In-session: dropping coords triggers the debounced effect → refetch by name.
      setCoords(null)
      return
    }
    // Proximity came from the server (reload / deep-link with lat,lng in the URL):
    // coords is already null, so setCoords wouldn't fire the effect. Fetch directly
    // to drop lat/lng and let the server flip `nearby` back to false.
    router.get(
      searchPath,
      { q: value.trim() || undefined },
      { preserveState: true, preserveScroll: true, replace: true, only: ["results", "query", "nearby"] }
    )
  }

  const searching = query.trim().length > 0

  return (
    <div data-testid="directory-screen-view" className="text-foreground">
      <ScreenHeader
        title={t("ui.react.features.user.directory.title")}
        className="md:hidden"
      />

      <div className="mx-auto w-full max-w-2xl px-4 py-4">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder={t("ui.react.features.user.directory.search_placeholder")}
            aria-label={t("ui.react.features.user.directory.search_placeholder")}
            data-testid="directory-search"
            className="h-11 w-full rounded-full border bg-card pl-10 pr-10 text-sm outline-none transition-colors focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-search-cancel-button]:hidden"
          />
          {value && (
            <button
              type="button"
              onClick={() => setValue("")}
              aria-label={t("ui.common.actions.close")}
              className="absolute right-2 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {nearby ? (
            <button
              type="button"
              onClick={clearProximity}
              data-testid="directory-proximity-active"
              className="inline-flex items-center gap-1.5 rounded-full bg-brand/12 px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-brand/20"
            >
              <Navigation className="size-3.5 text-brand" />
              {t("ui.react.features.user.directory.sorted_by_distance")}
              <X className="size-3.5 text-muted-foreground" />
            </button>
          ) : (
            <button
              type="button"
              onClick={useMyLocation}
              disabled={locating}
              data-testid="directory-near-me"
              className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-muted disabled:opacity-60"
            >
              {locating ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Navigation className="size-3.5" />
              )}
              {locating
                ? t("ui.react.features.user.directory.locating")
                : t("ui.react.features.user.directory.near_me")}
            </button>
          )}
          {geoError && (
            <span className="text-xs text-muted-foreground" data-testid="directory-geo-error">
              {t("ui.react.features.user.directory.location_denied")}
            </span>
          )}
        </div>

        {results.length === 0 ? (
          <p
            className="py-12 text-center text-sm text-muted-foreground"
            data-testid="directory-empty"
          >
            {searching
              ? t("ui.react.features.user.directory.no_results", { query: query.trim() })
              : t("ui.react.features.user.directory.empty")}
          </p>
        ) : (
          <div className="mt-4 divide-y divide-border/60" data-testid="directory-results">
            {results.map((location) => (
              <RestaurantRow key={location.id} location={location} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function RestaurantRow({
  location,
}: {
  location: DirectoryLocation
}) {
  // A row is a link to the restaurant's pickup menu, and that is all this screen
  // does. It used to POST /pickup/start, which created a Visit and an open Bill
  // before the diner had decided anything — going back left both behind. The
  // slug *is* the context now, so browsing away costs nothing.
  //
  // A restaurant that does not take pickup orders stays listed, because it is
  // still worth finding, but the row stops pretending to be an action the server
  // would refuse. `pickupSlug` is null in exactly that case.
  const reachable = location.pickupEnabled && Boolean(location.pickupSlug)
  const rowClassName = cn(
    "flex w-full items-center gap-3 py-3.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    !reachable && "opacity-60"
  )
  const testId = `directory-restaurant-${location.id}`

  const body = (
    <>
      <span className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-muted text-muted-foreground">
        {location.logoUrl ? (
          <img src={location.logoUrl} alt="" className="size-full object-cover" />
        ) : (
          <Store className="size-5" />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold leading-tight tracking-tight">
          {location.name}
        </span>
        {location.address && (
          <span className="flex items-center gap-1 truncate text-xs text-muted-foreground">
            <MapPin className="size-3 shrink-0" />
            {location.address}
          </span>
        )}
      </span>
      <span className="flex shrink-0 flex-col items-end gap-1">
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium text-foreground",
            location.openNow ? "bg-status-available/12" : "bg-muted"
          )}
        >
          <span
            className={cn(
              "size-1.5 rounded-full",
              location.openNow ? "bg-status-available" : "bg-muted-foreground/50"
            )}
            aria-hidden="true"
          />
          {location.openNow
            ? t("ui.react.features.user.directory.open")
            : t("ui.react.features.user.directory.closed")}
        </span>
        {location.distanceKm != null && (
          <span className="text-xs font-medium text-muted-foreground" data-testid="directory-distance">
            {t("ui.react.features.user.directory.distance_km", {
              value: location.distanceKm.toLocaleString("es-MX", { maximumFractionDigits: 1 }),
            })}
          </span>
        )}
        {!location.pickupEnabled && (
          <span
            className="text-xs text-muted-foreground"
            data-testid={`directory-pickup-unavailable-${location.id}`}
          >
            {t("ui.react.features.user.directory.pickup_unavailable")}
          </span>
        )}
      </span>
    </>
  )

  if (!reachable) {
    return (
      <div aria-disabled data-testid={testId} className={rowClassName}>
        {body}
      </div>
    )
  }

  return (
    <Link href={`/r/${location.pickupSlug}`} data-testid={testId} className={rowClassName}>
      {body}
    </Link>
  )
}
